"use client";

import { useEffect } from "react";
import Link from "next/link";

import { Button } from "@/components/ui/button";

/**
 * Root layout error boundary. Replaces the entire document when the root
 * layout itself throws, so it must supply its own `<html>` / `<body>`.
 *
 * Same rule as `src/app/error.tsx` and `src/app/admin/error.tsx`: never paint
 * `error.message` for a guest. Log the full report; show a digest only.
 */

export const GLOBAL_ERROR_LOG_PREFIX = "[global-error-boundary]";

export type GlobalErrorReport = {
  digest: string | null;
  message: string;
  name: string;
};

export function globalErrorDigest(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const { digest } = error as { digest?: unknown };
  if (typeof digest !== "string") return null;
  const trimmed = digest.trim();
  return trimmed === "" ? null : trimmed;
}

const RuntimeError = globalThis.Error;

function errorMessageAndName(error: unknown): { message: string; name: string } {
  if (error instanceof RuntimeError) {
    return { message: error.message, name: error.name };
  }
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const message = record.message;
    if (typeof message === "string") {
      const name = record.name;
      return {
        message,
        name: typeof name === "string" ? name : "Error",
      };
    }
  }
  return { message: String(error), name: typeof error };
}

export function buildGlobalErrorReport(error: unknown): GlobalErrorReport {
  const { message, name } = errorMessageAndName(error);
  return {
    digest: globalErrorDigest(error),
    message,
    name,
  };
}

export default function GlobalError({
  error,
  reset,
}: {
  error: InstanceType<typeof RuntimeError> & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(GLOBAL_ERROR_LOG_PREFIX, buildGlobalErrorReport(error));
  }, [error]);

  const digest = globalErrorDigest(error);

  return (
    <html lang="en">
      <body>
        <div
          style={{
            display: "flex",
            minHeight: "100vh",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "1.5rem",
            textAlign: "center",
            fontFamily:
              "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
          }}
        >
          <h2 style={{ fontSize: "1.5rem", fontWeight: 600, margin: 0 }}>
            Something went wrong
          </h2>
          <p style={{ maxWidth: "28rem", color: "#52525b", margin: 0 }}>
            This page hit an unexpected error. Anything you already submitted is
            safe. Try again, or go back to the home page.
          </p>
          {digest ? (
            <p style={{ fontSize: "0.75rem", color: "#71717a", margin: 0 }}>
              Reference:{" "}
              <span style={{ fontFamily: "ui-monospace, monospace" }}>
                {digest}
              </span>
            </p>
          ) : null}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            <Button type="button" onClick={() => reset()}>
              Try again
            </Button>
            <Button asChild variant="outline">
              <Link
                href="/"
                onNavigate={(event) => {
                  event.preventDefault();
                  // Recover the entire document when the root layout failed.
                  window.location.assign(window.location.origin);
                }}
              >
                Go home
              </Link>
            </Button>
          </div>
        </div>
      </body>
    </html>
  );
}
